import { type FC, useState } from "react";
import { ActionButton, ActionButtonIntent, ActionButtonSize, HStack, SymbolIcon } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { MAX_ROW_COUNT } from "@/ui-modules/core/grid/settings.ts";

import { pluralizeWord } from "../../../app-utils/string.js";
import style from "../../genome-viewer/styles/GenomeViewerTracksList.module.scss";
import { TooltipController } from "../overlays/components/OverlayControllers.js";

export interface ITableDataIndicatorProps {
  numRowsSelected?: number;
  numTotalRows: number;
  numRowsFiltered: number;
  numInitialRows?: number;
  rowEntityLabel?: string;
  filterIsSet: boolean;
  selectionVerb?: string;

  onResetFilter?: () => void;
}

const TableDataIndicator: FC<ITableDataIndicatorProps> = (props) => {
  const [show, setShow] = useState(true);

  const TABLE_TRUNCATED_MESSAGE = `Only the first ${formatNumberByThousands(
    MAX_ROW_COUNT,
  )} rows are shown, please refine with filters.`;

  const { numRowsSelected, numRowsFiltered, numTotalRows, numInitialRows, rowEntityLabel, filterIsSet } = props;

  function showIndicator() {
    return (
      numRowsSelected > 0 ||
      filterIsSet ||
      numRowsFiltered > MAX_ROW_COUNT ||
      (numInitialRows && numTotalRows !== numInitialRows)
    );
  }

  if (!showIndicator()) {
    return null;
  }

  const entityLabel: string = rowEntityLabel ? rowEntityLabel : "row";

  return (
    <>
      {show ? (
        <div id="rows-selected-indicator" className="row-selected-indicator">
          {numRowsSelected > 0 && (
            <HStack>
              <SymbolIcon icon={"checklist"} style={{ color: "var(--light-background" }} />
              <span>
                {formatNumberByThousands(numRowsSelected)} {pluralizeWord(entityLabel, numRowsSelected)}{" "}
                {props.selectionVerb || "selected"}
              </span>
            </HStack>
          )}
          {filterIsSet && (
            <HStack>
              <SymbolIcon icon={"filter_alt"} style={{ color: "var(--light-background" }} />
              <span>
                {formatNumberByThousands(numRowsFiltered)} / {formatNumberByThousands(numTotalRows)}{" "}
              </span>
              {props.onResetFilter && (
                <ActionButton label={"Reset"} size={ActionButtonSize.InTableCell} onPress={props.onResetFilter} />
              )}
            </HStack>
          )}

          {numInitialRows && numTotalRows !== numInitialRows && (
            <p>
              <u>Information</u>: Not all items that were originally part of this group are visible
            </p>
          )}

          {numRowsFiltered > MAX_ROW_COUNT && <p>{TABLE_TRUNCATED_MESSAGE}</p>}
          <ActionButton
            onPress={() => setShow(false)}
            icon={"close"}
            intent={ActionButtonIntent.Secondary}
            size={ActionButtonSize.InTableCell}
            className={"close-row-selected-indicator"}
          />
        </div>
      ) : (
        // Uses the same mechanism as for genome viewer keyboard shortcuts
        <TooltipController content={"Show table info"} overlayOptions={{ placement: "left" }}>
          {(tooltipTriggerProps) => (
            <div {...tooltipTriggerProps} className={style.keyboard_shortcuts_help} onClick={() => setShow(true)}>
              <SymbolIcon icon={"info"} style={{ color: "var(--light-background" }} />
            </div>
          )}
        </TooltipController>
      )}
    </>
  );
};

export default TableDataIndicator;
